export const SCHEDULE_TERMINAL_BALANCE_TOLERANCE = 1.00;

/**
 * Terminal rows of a schedule — one per investor tranche.
 *
 * The engine writes outstanding_balance PER INVESTOR TRANCHE, never contract-level:
 * a multi-investor schedule holds one row per investor per period, and each row
 * carries only that investor's balance. So "the schedule pays down to zero" means
 * EVERY tranche terminates at zero, not just whichever row happens to sort last.
 *
 * Reading only schedule[length - 1] (the previous behaviour) inspected a single
 * tranche, so a non-zero residual on any other investor was invisible.
 *
 * Rows are ordered `sequence_number, investor_id`, so the last occurrence of each
 * investor_id is that tranche's terminal row. When no row carries an investor_id
 * (single-investor contracts, and pre-T3-055 snapshots) this yields exactly one
 * terminal row — the last — i.e. identical behaviour to before.
 */
/**
 * Parse a persisted balance strictly. Number(null) / Number('') are 0, which would
 * let a MISSING balance read as a paid-down tranche and pass the check silently —
 * so null, undefined and blank are treated as non-finite.
 * Kept in step with the API copy at capitalmanagement-api/utils/scheduleValidation.js.
 */
const parseBalance = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

const getTerminalRows = (schedule) => {
  if (!Array.isArray(schedule) || schedule.length === 0) return [];

  const hasInvestors = schedule.some((row) => row?.investor_id != null);
  if (!hasInvestors) return [schedule[schedule.length - 1]];

  // Last row per investor_id, keyed to preserve one terminal row per tranche.
  // Rows without an investor_id are grouped under a single null key.
  const lastByInvestor = new Map();
  schedule.forEach((row) => {
    const key = row?.investor_id != null ? row.investor_id : null;
    lastByInvestor.set(key, row);
  });
  return Array.from(lastByInvestor.values());
};

/**
 * Sum of the per-investor terminal balances — the contract-level terminal balance.
 * Matches the summed Outstanding Balance column shown in the schedule tables.
 * Returns null when no finite balance can be resolved.
 */
export const getTerminalBalance = (schedule) => {
  const rows = getTerminalRows(schedule);
  if (rows.length === 0) return null;

  let sum = 0;
  for (const row of rows) {
    const v = parseBalance(row?.outstanding_balance);
    if (Number.isNaN(v)) return null;
    sum += v;
  }
  return sum;
};

/**
 * True when EVERY investor tranche terminates within tolerance. A single tranche
 * left non-zero fails the check even if the others net it out to zero.
 */
export const isTerminalBalanceZero = (schedule) => {
  const rows = getTerminalRows(schedule);
  if (rows.length === 0) return false;

  return rows.every((row) => {
    const v = parseBalance(row?.outstanding_balance);
    return !Number.isNaN(v) && Math.abs(v) <= SCHEDULE_TERMINAL_BALANCE_TOLERANCE;
  });
};

// Unused (no consumers): AmortizationScheduleTable computes its own runningBalances
// array and checks it inline. Kept for API symmetry — if it gains a consumer, note
// that it takes a precomputed balance array, not schedule rows, so it is NOT
// tranche-aware the way isTerminalBalanceZero above is.
export const isRunningBalanceZero = (runningBalances) => {
  if (!Array.isArray(runningBalances) || runningBalances.length === 0) return false;
  const last = Number(runningBalances[runningBalances.length - 1]);
  if (!Number.isFinite(last)) return false;
  return Math.abs(last) <= SCHEDULE_TERMINAL_BALANCE_TOLERANCE;
};
